import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

import styles from './styles.module.css';
import {
  PowerChartDeltaProps,
  DeltaMap,
  getKeyColor,
  calcRootMinHeight,
  calcXAxisTicks,
  formatValue,
  formatDuration,
  tsFromData,
  computeYTicks,
  computeDelta,
  EyeOnIcon,
  EyeOffIcon,
  CursorClearLabel,
  CustomXAxisTick
} from './utils';
import EmptyData from '../Form/EmptyData';
import BackChevronIcon from '@/assets/icons/back-chevron-arrow.svg?react';
import CrossSvg from '@/assets/icons/crossSmall.svg?react';
import EyeOnSvg from '@/assets/icons/eye.svg?react';
import EyeOffSvg from '@/assets/icons/eyeOff.svg?react';
import ForwardChevronIcon from '@/assets/icons/forward-chevron-arrow.svg?react';
import WarningSvg from '@/assets/icons/warning.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import {
  formatChartLinearTimestamp,
  formatTickDate,
  formatTickTime
} from '@/components/PowerChart/utils';

const WarningIcon = () => <WarningSvg width="16" height="16" />;

export default function PowerChartDelta({
  data,
  isLoading = false,
  onCursorChange,
  chartMetrics,
  onRemoveMetric,
  errors,
  onNavigateBack,
  onNavigateForward,
  canNavigateForward = true,
  navigateBackLabel = 'Previous interval',
  navigateForwardLabel = 'Next interval',
  timezone
}: PowerChartDeltaProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [blueIdx, setBlueIdx] = useState<number | null>(null);
  const [redIdx, setRedIdx] = useState<number | null>(null);
  const [bluePixelX, setBluePixelX] = useState(0);
  const [redPixelX, setRedPixelX] = useState(0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [hoverPixelX, setHoverPixelX] = useState(0);
  const [activeCursor, setActiveCursor] = useState<'blue' | 'red'>('blue');
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());
  const [showTooltips, setShowTooltips] = useState(true);

  const dataKeys = useMemo(
    () =>
      chartMetrics
        .map(metric => metric.id)
        .filter(id => data.length > 0 && id in data[0]),
    [chartMetrics, data]
  );
  const metricLabelById = useMemo(
    () =>
      Object.fromEntries(
        chartMetrics.map(metric => [metric.id, metric.label ?? metric.id])
      ),
    [chartMetrics]
  );
  const lineColors = useMemo(() => dataKeys.map(getKeyColor), [dataKeys]);
  const xAxisTicks = useMemo(() => calcXAxisTicks(data), [data]);
  const visibleKeys = useMemo(
    () => dataKeys.filter(k => !hiddenLines.has(k)),
    [dataKeys, hiddenLines]
  );
  const yTicks = useMemo(
    () => computeYTicks(data, visibleKeys),
    [data, visibleKeys]
  );
  const rootMinHeight = useMemo(
    () => calcRootMinHeight(dataKeys.length),
    [dataKeys.length]
  );

  const bluePoint = blueIdx !== null ? data[blueIdx] : null;
  const redPoint = redIdx !== null ? data[redIdx] : null;
  const hoverPoint = hoverIdx !== null ? data[hoverIdx] : null;
  const isHoverOnSelectedCursor =
    hoverIdx !== null && (hoverIdx === blueIdx || hoverIdx === redIdx);

  const delta: DeltaMap | null = useMemo(
    () =>
      bluePoint && redPoint
        ? computeDelta(bluePoint, redPoint, visibleKeys)
        : null,
    [bluePoint, redPoint, visibleKeys]
  );

  const cursorTimeDelta = useMemo(() => {
    if (!bluePoint || !redPoint) return null;
    const blueMs = new Date(String(bluePoint.timestamp)).getTime();
    const redMs = new Date(String(redPoint.timestamp)).getTime();
    if (isNaN(blueMs) || isNaN(redMs)) return null;
    return formatDuration(redMs - blueMs);
  }, [bluePoint, redPoint]);

  useEffect(() => {
    if (bluePoint && redPoint) {
      const blueTime = new Date(String(bluePoint.timestamp)).getTime();
      const redTime = new Date(String(redPoint.timestamp)).getTime();
      if (!isNaN(blueTime) && !isNaN(redTime) && redTime <= blueTime) {
        setBlueIdx(null);
        setBluePixelX(0);
        setRedIdx(null);
        setRedPixelX(0);
        setActiveCursor('blue');
      }
    }
  }, [bluePoint, redPoint]);

  useEffect(() => {
    if (onCursorChange)
      onCursorChange(bluePoint ?? null, redPoint ?? null, delta);
  }, [bluePoint, redPoint, delta]);

  const handleMouseMove = useCallback((e: any) => {
    if (e?.activeTooltipIndex !== undefined) {
      setHoverIdx(e.activeTooltipIndex);
      if (e?.activeCoordinate?.x !== undefined) {
        setHoverPixelX(e.activeCoordinate.x);
      }
    }
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHoverIdx(null);
    setHoverPixelX(0);
  }, []);

  const handleClick = useCallback(
    (e: any) => {
      if (e?.activeTooltipIndex === undefined || !e?.activeCoordinate) return;
      const idx = e.activeTooltipIndex as number;
      const px = e.activeCoordinate.x as number;
      const clickTs = tsFromData(data, idx);

      if (activeCursor === 'blue' || blueIdx === null) {
        const redTs = tsFromData(data, redIdx);
        if (!isNaN(redTs) && clickTs >= redTs) setRedIdx(null);
        setBlueIdx(idx);
        setBluePixelX(px);
        setActiveCursor('red');
        return;
      }

      const blueTs = tsFromData(data, blueIdx);
      if (clickTs <= blueTs) {
        setBlueIdx(idx);
        setBluePixelX(px);
        setRedIdx(null);
        setActiveCursor('red');
        return;
      }

      setRedIdx(idx);
      setRedPixelX(px);
      setActiveCursor('blue');
    },
    [activeCursor, blueIdx, redIdx, data]
  );

  const clearBlue = useCallback(() => {
    setBlueIdx(null);
    setBluePixelX(0);
    setRedIdx(null);
    setRedPixelX(0);
    setActiveCursor('blue');
  }, []);

  const clearRed = useCallback(() => {
    setRedIdx(null);
    setRedPixelX(0);
    setActiveCursor('red');
  }, []);

  const toggleLine = useCallback((key: string) => {
    setHiddenLines(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleAllLines = useCallback(() => {
    setHiddenLines(prev => {
      const allHidden = dataKeys.length > 0 && dataKeys.every(k => prev.has(k));
      return allHidden ? new Set() : new Set(dataKeys);
    });
  }, [dataKeys]);

  const allHidden =
    dataKeys.length > 0 && dataKeys.every(k => hiddenLines.has(k));

  const containerWidth = containerRef.current?.offsetWidth ?? 0;
  const clampTooltipLeft = (px: number) => {
    const w = 210;
    return px + w + 10 > containerWidth ? px - w - 10 : px + 10;
  };

  const renderMetricTooltip = (
    point: (typeof data)[number],
    pixelX: number,
    extraClassName?: string
  ) => (
    <div
      className={`${styles.dataWindow}${extraClassName ? ` ${extraClassName}` : ''}`}
      style={{ left: clampTooltipLeft(pixelX) }}
    >
      {dataKeys
        .filter(k => !hiddenLines.has(k))
        .map((key, i) => (
          <div key={key} className={styles.dataRow}>
            <span className={styles.dataValue} style={{ color: lineColors[i] }}>
              {formatValue(point[key])}
            </span>
            <span className={styles.dataLabel}>
              {metricLabelById[key] ?? key}
            </span>
          </div>
        ))}
    </div>
  );

  return (
    <div className={styles.root} style={{ minHeight: rootMinHeight }}>
      <div className={styles.chartArea} ref={containerRef}>
        {onNavigateBack && (
          <button
            id="summaryHistoryRangePrevious"
            type="button"
            className={`${styles.rangeNavButton} ${styles.rangeNavButtonLeft}`}
            aria-label={navigateBackLabel}
            onClick={onNavigateBack}
          >
            <BackChevronIcon
              className={styles.rangeNavIcon}
              width={24}
              height={24}
            />
          </button>
        )}
        {onNavigateForward && (
          <button
            id="summaryHistoryRangeNext"
            type="button"
            className={`${styles.rangeNavButton} ${styles.rangeNavButtonRight}`}
            aria-label={navigateForwardLabel}
            onClick={onNavigateForward}
            disabled={!canNavigateForward}
          >
            <ForwardChevronIcon
              className={styles.rangeNavIcon}
              width={24}
              height={24}
            />
          </button>
        )}
        {isLoading ? (
          <div className={styles.loader}>
            <ContainerLoader />
          </div>
        ) : !data.length ? (
          <div className={styles.loader}>
            <EmptyData label="No history data available" />
          </div>
        ) : (
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 46, right: 40, left: 10, bottom: 0 }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                style={{ cursor: 'crosshair' }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-light-blue-grid)"
                  horizontal
                  vertical
                />
                <XAxis
                  dataKey="timestamp"
                  height={80}
                  tick={<CustomXAxisTick timezone={timezone} />}
                  tickFormatter={value =>
                    formatTickDate(String(value), timezone)
                  }
                  ticks={xAxisTicks}
                  interval={0}
                  stroke="var(--color-light-blue-grid)"
                  label={{ value: 'Date/Time', position: 'insideBottom' }}
                />
                <YAxis
                  scale="linear"
                  stroke="var(--color-light-blue-grid)"
                  tick={false}
                  width={10}
                  ticks={yTicks}
                />

                {hoverIdx !== null && blueIdx === null && (
                  <ReferenceLine
                    x={data[hoverIdx]?.timestamp}
                    stroke="var(--main-blue-color)"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    strokeOpacity={0.5}
                  />
                )}
                {hoverIdx !== null && blueIdx !== null && redIdx === null && (
                  <ReferenceLine
                    x={data[hoverIdx]?.timestamp}
                    stroke="var(--color-ws-disconnected)"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    strokeOpacity={0.5}
                  />
                )}
                {blueIdx !== null && (
                  <ReferenceLine
                    x={data[blueIdx]?.timestamp}
                    stroke="var(--main-blue-color)"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={
                      <CursorClearLabel
                        color="var(--main-blue-color)"
                        onClear={clearBlue}
                      />
                    }
                  />
                )}
                {redIdx !== null && (
                  <ReferenceLine
                    x={data[redIdx]?.timestamp}
                    stroke="var(--color-ws-disconnected)"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={
                      <CursorClearLabel
                        color="var(--color-ws-disconnected)"
                        onClear={clearRed}
                      />
                    }
                  />
                )}

                {dataKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="linear"
                    dataKey={key}
                    stroke={lineColors[i]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    name={key}
                    hide={hiddenLines.has(key)}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            {blueIdx !== null && bluePoint && (
              <div
                className={styles.cursorTimestamp}
                style={{ left: bluePixelX }}
              >
                <span className={styles.cursorDate}>
                  {formatTickDate(String(bluePoint.timestamp), timezone)}
                </span>
                <span className={styles.cursorTime}>
                  {formatTickTime(String(bluePoint.timestamp), timezone)}
                </span>
              </div>
            )}
            {redIdx !== null && redPoint && (
              <div
                className={styles.cursorTimestamp}
                style={{ left: redPixelX }}
              >
                <span className={styles.cursorDate}>
                  {formatTickDate(String(redPoint.timestamp), timezone)}
                </span>
                <span className={styles.cursorTime}>
                  {formatTickTime(String(redPoint.timestamp), timezone)}
                </span>
              </div>
            )}

            {hoverPoint && !isHoverOnSelectedCursor && (
              <div
                className={styles.hoverTooltipStack}
                style={{ left: clampTooltipLeft(hoverPixelX) }}
              >
                <div className={styles.cursorTimestamp}>
                  <span className={styles.cursorDate}>
                    {formatTickDate(String(hoverPoint.timestamp), timezone)}
                  </span>
                  <span className={styles.cursorTime}>
                    {formatTickTime(String(hoverPoint.timestamp), timezone)}
                  </span>
                </div>
                {showTooltips && (
                  <div
                    className={`${styles.dataWindow} ${styles.dataWindowStacked}`}
                  >
                    {dataKeys
                      .filter(k => !hiddenLines.has(k))
                      .map((key, i) => (
                        <div key={key} className={styles.dataRow}>
                          <span
                            className={styles.dataValue}
                            style={{ color: lineColors[i] }}
                          >
                            {formatValue(hoverPoint[key])}
                          </span>
                          <span className={styles.dataLabel}>
                            {metricLabelById[key] ?? key}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {showTooltips &&
              bluePoint &&
              renderMetricTooltip(bluePoint, bluePixelX, styles.dataWindowBlue)}

            {showTooltips &&
              redPoint &&
              renderMetricTooltip(redPoint, redPixelX, styles.dataWindowRed)}
          </div>
        )}
      </div>

      <div className={styles.legendPanel}>
        <div className={styles.legendHeader}>
          <div className={`${styles.lc} ${styles.lcLabel}`}>PEN LABEL</div>
          <div className={`${styles.lc} ${styles.lcEye}`}>
            <button
              className={styles.eyeBtn}
              onClick={toggleAllLines}
              type="button"
            >
              {allHidden ? <EyeOffIcon /> : <EyeOnIcon />}
            </button>
          </div>
          <div className={`${styles.lc} ${styles.lcVal}`}>
            <span className={`${styles.headerDot} ${styles.headerDotBlue}`} />
            <span>VAL</span>
          </div>
          <div className={`${styles.lc} ${styles.lcVal}`}>
            <span className={`${styles.headerDot} ${styles.headerDotRed}`} />
            <span>VAL</span>
          </div>
          <div className={`${styles.lc} ${styles.lcDelta}`}>DELTA</div>
          <div className={`${styles.lc} ${styles.lcRemove}`} />
        </div>

        <div className={styles.legendBody}>
          {chartMetrics.map((metric, i) => {
            const isOnDataKeys = dataKeys.includes(metric.id);
            const isHidden = hiddenLines.has(metric.id) || !isOnDataKeys;
            const color = getKeyColor(metric.id);
            const metricErrorMessages =
              errors
                ?.flatMap(err => err.details?.errors ?? [])
                .filter(d => d.metric === metric.id)
                .map(d => d.errorMessage) ?? [];
            const hasError = metricErrorMessages.length > 0;
            return (
              <div
                key={metric.id}
                className={`${styles.legendRow} ${i % 2 === 0 ? styles.legendRowAlt : ''} ${isHidden ? styles.legendRowHidden : ''}`}
              >
                <div className={`${styles.lc} ${styles.lcLabel}`}>
                  <span
                    className={styles.legendPill}
                    style={{ backgroundColor: color }}
                  />
                  <span className={styles.legendNameContainer}>
                    <span className={styles.legendName}>
                      {metric.label ?? metric.id}
                    </span>
                    <span className={styles.legendTooltip}>
                      {metric.label ?? metric.id}
                    </span>
                  </span>
                </div>
                <div className={`${styles.lc} ${styles.lcEye}`}>
                  {hasError ? (
                    <span className={styles.warningIconContainer}>
                      <span className={styles.warningIcon}>
                        <WarningIcon />
                      </span>
                      <span className={styles.legendTooltip}>
                        {metricErrorMessages.join(' • ')}
                      </span>
                    </span>
                  ) : (
                    <button
                      className={styles.eyeBtn}
                      onClick={() => toggleLine(metric.id)}
                      type="button"
                    >
                      {isHidden ? <EyeOffIcon /> : <EyeOnIcon />}
                    </button>
                  )}
                </div>
                <div className={`${styles.lc} ${styles.lcVal}`}>
                  {bluePoint && !isHidden ? (
                    <span className={styles.valText}>
                      {formatValue(bluePoint[metric.id])}
                    </span>
                  ) : (
                    <span className={styles.valEmpty}>—</span>
                  )}
                </div>
                <div className={`${styles.lc} ${styles.lcVal}`}>
                  {redPoint && !isHidden ? (
                    <span className={styles.valText}>
                      {formatValue(redPoint[metric.id])}
                    </span>
                  ) : (
                    <span className={styles.valEmpty}>—</span>
                  )}
                </div>
                <div className={`${styles.lc} ${styles.lcDelta}`}>
                  {delta && !isHidden ? (
                    <span className={styles.valText}>{delta[metric.id]}</span>
                  ) : (
                    <span className={styles.valEmpty}>—</span>
                  )}
                </div>
                <div className={`${styles.lc} ${styles.lcRemove}`}>
                  {onRemoveMetric && (
                    <button
                      className={styles.removeBtn}
                      type="button"
                      onClick={() => onRemoveMetric(metric)}
                    >
                      <CrossSvg width="12" height="12" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.cursorTable}>
          <div className={styles.cursorTableHeader}>
            <div className={styles.cursorTableCell}>
              <span className={`${styles.headerDot} ${styles.headerDotBlue}`} />
              <span>BLUE CURSOR</span>
            </div>
            <div className={styles.cursorTableCell}>
              <span className={`${styles.headerDot} ${styles.headerDotRed}`} />
              <span>RED CURSOR</span>
            </div>
            <div className={styles.cursorTableCell}>DELTA</div>
          </div>
          <div className={styles.cursorTableRow}>
            <div className={styles.cursorTableCell}>
              {bluePoint ? (
                <span className={styles.cursorTs}>
                  {formatChartLinearTimestamp(
                    String(bluePoint.timestamp),
                    timezone
                  )}
                </span>
              ) : (
                <span className={styles.valEmpty}>—</span>
              )}
            </div>
            <div className={styles.cursorTableCell}>
              {redPoint ? (
                <span className={styles.cursorTs}>
                  {formatChartLinearTimestamp(
                    String(redPoint.timestamp),
                    timezone
                  )}
                </span>
              ) : (
                <span className={styles.valEmpty}>—</span>
              )}
            </div>
            <div className={styles.cursorTableCell}>
              {cursorTimeDelta ? (
                <span className={styles.cursorTs}>{cursorTimeDelta}</span>
              ) : (
                <span className={styles.valEmpty}>—</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.legendFooter}>
          <button
            className={`${styles.toggleBtn} ${!showTooltips ? styles.toggleBtnOff : ''}`}
            onClick={() => setShowTooltips(v => !v)}
            type="button"
          >
            {showTooltips ? (
              <EyeOffSvg width="16" height="16" />
            ) : (
              <EyeOnSvg width="16" height="16" />
            )}
            {showTooltips ? 'Hide Events' : 'Show Events'}
          </button>
        </div>
      </div>
    </div>
  );
}
